/**
 * @voltxampere/engine — TRIAC bidirectional latch stamp (Sprint 54.2b-1)
 *
 * Ideal-switch TRIAC companion model with state machine off/triggered/
 * latched. ngspice BT136 subcircuit parity reference; canonical engine
 * type `triac`.
 *
 * State plumbing — augmented internal-node pattern (BJT dynamic
 * augments lineage Sprint 24 B1):
 *
 *   The stamp allocates one `internalNode` row per TRIAC instance via
 *   `dynamicAugments()`. The row's MNA variable X[stateRow] holds the
 *   conducting state as 0 (off) or 1 (on). DC stamp pins it to 0;
 *   transient stamp computes the new state from xStepPrev (previous
 *   converged time step's state + branch voltage + gate trigger)
 *   and pins X[stateRow] to that value. Newton iters within a step
 *   read X[stateRow] as a frozen constant (it's pinned per-iter by
 *   the identity row).
 *
 *   This avoids extending DCStampContext with a new `stateHistory`
 *   map — the augmented row reuses the existing `xStepPrev` mechanism
 *   that capacitor / inductor stamps already depend on.
 *
 * State machine (transient time step n):
 *
 *   prevState  = xStepPrev[stateRow]                  // 0 or 1
 *   gateTrigger = V_gate(xStepPrev) > V_GT             // proxy for I_gate > I_GT
 *   vMtPrev    = V_MT1(xStepPrev) − V_MT2(xStepPrev)
 *   wasLatched = prevState > 0.5 && |vMtPrev| > V_HOLD_VOLTAGE_PROXY
 *
 *   nowState = (gateTrigger || wasLatched) ? 1 : 0
 *
 * Branch stamp based on nowState:
 *
 *   nowState == 0 (off):     conductance G_off between MT1−MT2
 *   nowState == 1 (on):      Norton drive (low R_on + V_on offset)
 *
 * Conducting Norton uses bidirectional V_on offset (sign matches the
 * MT1-MT2 voltage direction so the device drops V_on regardless of
 * current polarity). For ideal-switch MVP we omit V_on transient
 * (always V_drop = V_on regardless of |I|) — Sprint 54.2c may add
 * SCR regenerative two-transistor model for tighter ngspice parity.
 *
 * Holding-current drop-out: when V_diff_prev (across MT1-MT2) is
 * very small (< V_HOLD_VOLTAGE_PROXY) AND no fresh gate trigger,
 * state flips to 0 next step. The proxy threshold (default 1V)
 * approximates "current below I_H" because in conducting state
 * V_drop ≈ V_on (1.5V) and dropping below that means the conducting
 * channel is collapsing. ngspice triac.cm uses real branch current
 * via internal node; we accept this approximation for MVP and
 * tighten in Sprint 54.3 parity validation.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
/**
 * Default conductance in the OFF state (Siemens). 1e-9 ≈ 1 GΩ
 * impedance — typical for a real TRIAC's reverse blocking, and
 * comfortably above gmin (1e-12) so the MNA LU pivot stays
 * well-conditioned without obscuring the OFF behaviour.
 */
const G_OFF_DEFAULT = 1e-9;
/**
 * Default ON-state resistance (Ω). 0.5 Ω matches BT136 V_T(BO) /
 * I_T = 1.5 V / 3 A typical conducting characteristic at rated
 * current. Higher than ideal-switch zero so the conducting branch
 * still injects a finite voltage drop.
 */
const R_ON_DEFAULT = 0.5;
/**
 * Default ON-state voltage drop (V). 1.5 V matches BT136 V_T at
 * mid-current. The Norton companion emits this as an offset
 * regardless of branch current sign (bidirectional).
 */
const V_ON_DEFAULT = 1.5;
/**
 * Gate trigger voltage threshold (V). Proxy for I_GT × R_gate-pin.
 * BT136 I_GT = 10 mA at 25°C; with R_gate_estimated ≈ 100 Ω the
 * voltage trigger crosses at ~1 V. Configurable per variant.
 */
const V_GT_DEFAULT = 1.0;
/**
 * Holding voltage proxy (V). Below this V_MT1-MT2 magnitude, the
 * latch drops out next step. Approximates I_H × R_on ≈ 10 mA ×
 * 0.5 Ω = 5 mV in ideal — but real circuits see noise; 1 V is a
 * pragmatic threshold that prevents false drop-out from
 * numerical jitter while still releasing the latch when V_diff
 * actually collapses (zero crossing on AC mains).
 */
const V_HOLD_VOLTAGE_PROXY_DEFAULT = 1.0;
/**
 * Sprint 54.4 — SCR uni-directional + GTO gate-turn-off param defaults.
 * `unidirectional = false`: TRIAC (bidirectional, default).
 *                   true:  SCR (forward conduction only, reverse blocks).
 * `gateTurnOff   = false`: TRIAC / SCR (latch only breaks at I_H below
 *                          holding current threshold).
 *                   true:  GTO (negative gate voltage > I_GQ also breaks
 *                          the latch).
 * `vGateTurnOff`: voltage proxy for the I_GQ gate-turn-off threshold
 *                 (negative magnitude). Datasheet typ. for BTW58: 1.5 V.
 */
const UNIDIRECTIONAL_DEFAULT = false;
const GATE_TURN_OFF_DEFAULT = false;
const V_GATE_TURN_OFF_DEFAULT = 1.5;
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Sprint 54.2b-1 — every TRIAC instance allocates exactly 1
 * internal node row for state storage. No dependency on params
 * (every TRIAC has state). Returns ["internalNode"] always.
 */
function triacDynamicAugments(_component) {
    return ["internalNode"];
}
/**
 * Stamp the OFF branch — high impedance between MT1 and MT2.
 * Symmetric four-corner conductance pattern with G_off.
 */
function stampOffBranch(ctx, mt1, mt2, gOff) {
    ctx.addY(mt1, mt1, gOff);
    ctx.addY(mt2, mt2, gOff);
    ctx.addY(mt1, mt2, -gOff);
    ctx.addY(mt2, mt1, -gOff);
}
/**
 * Stamp the ON branch — low R_on with V_on Norton offset.
 * Bidirectional: V_on sign mirrors V_MT1-V_MT2 polarity so the
 * device always drops V_on (not −V_on) toward the conducting
 * direction.
 *
 *   i_branch = (V_MT1 − V_MT2 − sign(V_diff) · V_on) / R_on
 *
 * Linearised around V_diff_prev:
 *
 *   Y[mt1][mt1] += 1/R_on,    Y[mt2][mt2] += 1/R_on
 *   Y[mt1][mt2] -= 1/R_on,    Y[mt2][mt1] -= 1/R_on
 *   rhs[mt1] += sign(V_diff_prev) · V_on / R_on
 *   rhs[mt2] -= sign(V_diff_prev) · V_on / R_on
 */
function stampOnBranch(ctx, mt1, mt2, rOn, vOn, vDiffPrev) {
    const gOn = 1 / rOn;
    ctx.addY(mt1, mt1, gOn);
    ctx.addY(mt2, mt2, gOn);
    ctx.addY(mt1, mt2, -gOn);
    ctx.addY(mt2, mt1, -gOn);
    const sign = vDiffPrev >= 0 ? 1 : -1;
    const ieq = (sign * vOn) / rOn;
    if (mt1 >= 0)
        ctx.rhs[mt1] = (ctx.rhs[mt1] ?? 0) - ieq;
    if (mt2 >= 0)
        ctx.rhs[mt2] = (ctx.rhs[mt2] ?? 0) + ieq;
}
/**
 * Pin the state row (internal node) to a specific value (0 or 1).
 * Identity equation: Y[stateRow][stateRow] = 1, RHS[stateRow] =
 * value. After LU the variable equals exactly `value`.
 */
function pinStateRow(ctx, stateRow, value) {
    ctx.addY(stateRow, stateRow, 1);
    ctx.rhs[stateRow] = value;
}
/**
 * DC stamp — TRIAC starts OFF in DC OP (no time history to draw
 * latching from). State row pinned to 0; branch is G_off.
 */
export const triacDCStamp = (ctx, component) => {
    const gOff = getNumericParam(component, "gOff", G_OFF_DEFAULT);
    if (!(gOff > 0) || !Number.isFinite(gOff)) {
        // Sprint 55 typed-error migration: StampParameterError carries the
        // structured context the Recovery Center needs (stampType +
        // parameter + received + expected) for an actionable Inspector hint.
        throw new StampParameterError(`triacDCStamp: component "${component.id}" has non-positive gOff (got ${String(gOff)})`, {
            meta: {
                stampType: "triac",
                parameter: "gOff",
                received: gOff,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    const mt1 = ctx.nodeIndex(getTerminalNetId(component, "mt1"));
    const mt2 = ctx.nodeIndex(getTerminalNetId(component, "mt2"));
    const stateRow = ctx.internalNodeIndex(component.id, 0);
    stampOffBranch(ctx, mt1, mt2, gOff);
    pinStateRow(ctx, stateRow, 0);
};
/**
 * Transient stamp — read previous state from xStepPrev, derive
 * new state from gate trigger + latch logic, stamp branch
 * accordingly. State row pinned to new state for this step.
 *
 * First-step special case (ctx.firstStep): force OFF — at t=0
 * xStepPrev is zero everywhere and the state-derivation proxies
 * would false-trigger.
 */
export const triacTransientStamp = (ctx, component) => {
    const gOff = getNumericParam(component, "gOff", G_OFF_DEFAULT);
    const rOn = getNumericParam(component, "rOn", R_ON_DEFAULT);
    const vOn = getNumericParam(component, "vOn", V_ON_DEFAULT);
    const vGateTrigger = getNumericParam(component, "vGateTrigger", V_GT_DEFAULT);
    const vHoldProxy = getNumericParam(component, "vHoldProxy", V_HOLD_VOLTAGE_PROXY_DEFAULT);
    // Sprint 54.4 — read SCR / GTO param overrides directly from
    // component.params (no helper for booleans + numerical defaults).
    const rawParams = component.params;
    const unidirectional = typeof rawParams.unidirectional === "boolean"
        ? rawParams.unidirectional
        : UNIDIRECTIONAL_DEFAULT;
    const gateTurnOff = typeof rawParams.gateTurnOff === "boolean" ? rawParams.gateTurnOff : GATE_TURN_OFF_DEFAULT;
    const vGateTurnOff = typeof rawParams.vGateTurnOff === "number" && Number.isFinite(rawParams.vGateTurnOff)
        ? rawParams.vGateTurnOff
        : V_GATE_TURN_OFF_DEFAULT;
    if (!(rOn > 0) || !Number.isFinite(rOn)) {
        // Sprint 55 typed-error migration.
        throw new StampParameterError(`triacTransientStamp: component "${component.id}" has non-positive rOn (got ${String(rOn)})`, {
            meta: {
                stampType: "triac",
                parameter: "rOn",
                received: rOn,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    if (vOn < 0 || !Number.isFinite(vOn)) {
        // Sprint 55 typed-error migration.
        throw new StampParameterError(`triacTransientStamp: component "${component.id}" has negative vOn (got ${String(vOn)})`, {
            meta: {
                stampType: "triac",
                parameter: "vOn",
                received: vOn,
                expected: ">= 0 and finite",
                componentId: component.id,
            },
        });
    }
    const mt1 = ctx.nodeIndex(getTerminalNetId(component, "mt1"));
    const mt2 = ctx.nodeIndex(getTerminalNetId(component, "mt2"));
    const gate = ctx.nodeIndex(getTerminalNetId(component, "gate"));
    const stateRow = ctx.internalNodeIndex(component.id, 0);
    let newState = 0;
    let vDiffPrev = 0;
    if (!ctx.firstStep) {
        // Read previous step's state and pin voltages.
        const prevStateRaw = nodeVoltage(ctx.xStepPrev, stateRow);
        const prevState = prevStateRaw > 0.5 ? 1 : 0;
        const vMt1Prev = nodeVoltage(ctx.xStepPrev, mt1);
        const vMt2Prev = nodeVoltage(ctx.xStepPrev, mt2);
        const vGatePrev = nodeVoltage(ctx.xStepPrev, gate);
        vDiffPrev = vMt1Prev - vMt2Prev;
        // Gate trigger: voltage proxy for I_gate > I_GT.
        //   - TRIAC: gate referenced to MT1 (BT136 datasheet convention).
        //     |V_gate - V_MT1| > V_GT triggers in either polarity.
        //   - SCR: gate referenced to CATHODE (= MT2 here; anode=MT1).
        //     V_gate - V_MT2 > V_GT (positive only) triggers; also requires
        //     forward bias V_MT1 > V_MT2 at the moment of trigger.
        const vGateToMt1 = vGatePrev - vMt1Prev;
        const vGateToMt2 = vGatePrev - vMt2Prev;
        const gateTrigger = unidirectional
            ? vGateToMt2 > vGateTrigger && vDiffPrev > 0
            : Math.abs(vGateToMt1) > vGateTrigger;
        // Sprint 54.4 — GTO gate-turn-off: a negative gate signal of
        // magnitude > V_GQ proxy (datasheet I_GQ analog) breaks the latch
        // even with holding current still flowing. Real GTO needs a
        // negative current; we approximate via V_gate_to_cathode (MT2 in
        // unidirectional mode) — when cathode-referenced gate goes below
        // -V_GQ proxy, latch breaks.
        const gateTurnOffTrigger = gateTurnOff && vGateToMt2 < -vGateTurnOff;
        // Holding-current drop-out: was latched AND |V_diff| still
        // above the drop-out threshold (proxy for I > I_H).
        // Sprint 54.4 — SCR drops out on either I_H AND reverse-direction
        // crossing (V_diff < 0 forces commutation regardless of magnitude).
        const reverseCommutated = unidirectional && vDiffPrev <= 0;
        const stillLatched = prevState === 1 &&
            Math.abs(vDiffPrev) > vHoldProxy &&
            !reverseCommutated &&
            !gateTurnOffTrigger;
        newState = gateTrigger || stillLatched ? 1 : 0;
    }
    // Pin state row to the derived value.
    pinStateRow(ctx, stateRow, newState);
    // Stamp the appropriate branch.
    if (newState === 1) {
        // Sprint 54.4 — SCR on-state conducts only forward direction
        // (V_MT1 > V_MT2). When unidirectional + reverse-biased, falls
        // back to off-branch impedance (gOff).
        if (unidirectional && vDiffPrev <= 0) {
            stampOffBranch(ctx, mt1, mt2, gOff);
        }
        else {
            stampOnBranch(ctx, mt1, mt2, rOn, vOn, vDiffPrev);
        }
    }
    else {
        stampOffBranch(ctx, mt1, mt2, gOff);
    }
};
/**
 * StampDefinition envelope. Static augments empty (state row is
 * dynamic — every instance allocates 1). Dynamic augments returns
 * ["internalNode"] unconditionally.
 *
 * No `junctionTerminals` — TRIAC has no p-n junction in the
 * Newton-step sense; the state logic is discrete (boolean latch),
 * not exp()-based.
 */
export const triacDCStampDefinition = defineStamp({
    type: "triac",
    augments: [],
    dynamicAugments: triacDynamicAugments,
    applyDC: triacDCStamp,
    applyTransient: triacTransientStamp,
});
/**
 * Transient envelope re-export for register.ts compatibility.
 */
export const triacTransientStampDefinition = triacDCStampDefinition;
//# sourceMappingURL=triac.js.map